/**
 * Apollo Client Fetch Policies
 *
 * Maps the previous React Query stale time concepts to Apollo fetch policies.
 * Use these constants for consistent data freshness behavior across the app.
 */

import type { WatchQueryFetchPolicy } from '@apollo/client'

/**
 * Fetch policies for different data freshness requirements
 *
 * ⛔ STATIC AND SEMI_STATIC ARE BOTH PLAIN `cache-first`. THE NAMES ARE NOT A
 *    FRESHNESS TIER -- they describe the AUTHOR'S INTENT, not any behaviour the
 *    constant provides. A `cache-first` query fetches ONCE per cache entry and
 *    then never again for the life of the tab unless you add something.
 *
 * ⛔ TWO FACTS THAT COST US A PRODUCTION BUG (TKT-20260915-0002, and ~120 call
 *    sites audited on 2026-09-15). Both are counter-intuitive; read them before
 *    choosing a policy:
 *
 *    1. `cache-first` DOES NOT RE-FETCH ON REMOUNT. Unmounting and remounting a
 *       component re-reads the same cache ENTRY; it does not go to the network.
 *       "It remounts anyway, so it refreshes" is false and has been used to
 *       justify leaving stale reads in place. Only a policy change, a poll, a
 *       different variables key, or an explicit refetch goes to the server.
 *
 *    2. A `pollInterval` ONLY FIRES WHILE THE CONSUMER STAYS MOUNTED. On a
 *       visit-and-leave screen a 10-minute poll essentially never fires, so
 *       pairing one there buys nothing -- `cache-and-network` is the fix.
 *       A poll is right for something long-lived (a session-wide hook, a board
 *       left open all service), which is exactly where a per-mount network read
 *       would be wasteful.
 *
 * HOW TO CHOOSE
 * - The user PICKS from it and someone else can create/edit it mid-session
 *     → DYNAMIC (`cache-and-network`). Paints from cache, revalidates behind it.
 * - Long-lived hook or screen, slow-moving data, per-mount fetch not justified
 *     → STATIC / SEMI_STATIC **plus** an explicit `pollInterval` from POLL_INTERVALS.
 * - Genuinely immutable within a session (a vocabulary only a deploy changes,
 *     an identity, a rate card)
 *     → STATIC / SEMI_STATIC alone. Say WHY in a comment, or the next reader
 *       cannot tell your decision from an oversight.
 *
 * ⛔ A QUERY ON A PERMANENTLY-MOUNTED SHEET NEEDS `skip: !open` AS WELL.
 *    If the parent renders the sheet unconditionally and only toggles an `open`
 *    prop, an ungated query fetches at PAGE mount and never on reopen -- so a
 *    policy change alone looks applied and does nothing. Gate it on `open` too.
 *
 * - REALTIME → network-only with pollInterval, or subscriptions
 * - NONE → network-only (always fresh)
 */
export const FETCH_POLICIES = {
  /** Static data that rarely changes - room types, statuses. Pair with POLL_INTERVALS.STATIC for background refresh. */
  STATIC: 'cache-first' as WatchQueryFetchPolicy,

  /** Semi-static data - settings, templates. Pair with POLL_INTERVALS.SEMI_STATIC for background refresh. */
  SEMI_STATIC: 'cache-first' as WatchQueryFetchPolicy,

  /** Dynamic data - reservations, calendar (default) */
  DYNAMIC: 'cache-and-network' as WatchQueryFetchPolicy,

  /** Real-time data - use subscriptions instead when possible */
  REALTIME: 'network-only' as WatchQueryFetchPolicy,

  /** Always fresh data */
  NONE: 'network-only' as WatchQueryFetchPolicy,

  /** No cache at all - for one-time fetches */
  NO_CACHE: 'no-cache' as WatchQueryFetchPolicy,
} as const

/**
 * Poll intervals for data that needs periodic refresh
 * Use subscriptions instead of polling when real-time updates are critical
 */
export const POLL_INTERVALS = {
  /** 30 minutes for static data (room types, statuses) */
  STATIC: 30 * 60 * 1000,

  /** 10 minutes for semi-static data */
  SEMI_STATIC: 10 * 60 * 1000,

  /** 2 minutes for dynamic data */
  DYNAMIC: 2 * 60 * 1000,

  /** 30 seconds for real-time data (prefer subscriptions) */
  REALTIME: 30 * 1000,

  /** 1 minute for dashboard metrics */
  DASHBOARD: 60 * 1000,

  /** 5 minutes for background refreshes */
  BACKGROUND: 5 * 60 * 1000,
} as const

/**
 * Next fetch policy after initial fetch
 * Used with watchQuery.nextFetchPolicy
 */
export const NEXT_FETCH_POLICIES = {
  /** After cache-and-network, use cache-first for subsequent reads */
  CACHE_FIRST: 'cache-first' as WatchQueryFetchPolicy,

  /** Keep using cache-and-network */
  CACHE_AND_NETWORK: 'cache-and-network' as WatchQueryFetchPolicy,
} as const

/**
 * Helper to get appropriate fetch policy based on data type
 */
export function getFetchPolicy(
  dataType: 'static' | 'semi-static' | 'dynamic' | 'realtime' | 'none'
): WatchQueryFetchPolicy {
  switch (dataType) {
    case 'static':
      return FETCH_POLICIES.STATIC
    case 'semi-static':
      return FETCH_POLICIES.SEMI_STATIC
    case 'dynamic':
      return FETCH_POLICIES.DYNAMIC
    case 'realtime':
      return FETCH_POLICIES.REALTIME
    case 'none':
      return FETCH_POLICIES.NONE
    default:
      return FETCH_POLICIES.DYNAMIC
  }
}

/**
 * Helper to get poll interval based on data type
 */
export function getPollInterval(
  dataType: 'static' | 'semi-static' | 'dynamic' | 'realtime' | 'dashboard'
): number {
  switch (dataType) {
    case 'static':
      return POLL_INTERVALS.STATIC
    case 'semi-static':
      return POLL_INTERVALS.SEMI_STATIC
    case 'dynamic':
      return POLL_INTERVALS.DYNAMIC
    case 'realtime':
      return POLL_INTERVALS.REALTIME
    case 'dashboard':
      return POLL_INTERVALS.DASHBOARD
    default:
      return POLL_INTERVALS.DYNAMIC
  }
}
